import React, { useEffect, useMemo, useState } from "react";
import Order from "./Order";
import arrowLeft from "../assets/images/navbar/arrow-left.png";
import cancel from "../assets/images/navbar/cancel.png";
import filter from "../assets/images/navbar/filter.png";
import downArrow from "../assets/images/navbar/down-arrow.png";
import { restaurantOrderService } from "../services";
import { Order as OrderType } from "../utils/types";

type OrderHistoryProps = {
  setIsOrderHistoryOpen: (open: boolean) => void;
};

const isSameDay = (date: Date, compare: Date) =>
  date.getFullYear() === compare.getFullYear() &&
  date.getMonth() === compare.getMonth() &&
  date.getDate() === compare.getDate();

const startOfWeek = (date: Date) => {
  const copy = new Date(date);
  const day = copy.getDay();
  copy.setDate(copy.getDate() - day);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const OrderHistory = ({ setIsOrderHistoryOpen }: OrderHistoryProps) => {
  const [activeTab, setActiveTab] = useState<string>("today");
  const [selectedOrderIndex, setSelectedOrderIndex] = useState<number | null>(null);
  const [orders, setOrders] = useState<OrderType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const [delivered, cancelled]: any[] = await Promise.all([
          restaurantOrderService.getOrders({ page: 1, limit: 50, orderStatus: "delivered" }),
          restaurantOrderService.getOrders({ page: 1, limit: 50, orderStatus: "cancelled" }),
        ]);
        const merged = [
          ...(delivered?.data || []),
          ...(cancelled?.data || []),
        ].sort(
          (a: OrderType, b: OrderType) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setOrders(merged);
      } catch (error) {
        console.error("Failed to load order history:", error);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, []);

  const filteredOrders = useMemo(() => {
    const now = new Date();
    const weekStart = startOfWeek(now);
    return orders.filter((order) => {
      const created = new Date(order.createdAt);
      if (activeTab === "cancelled") return order.orderStatus === "cancelled";
      if (activeTab === "today") {
        return order.orderStatus !== "cancelled" && isSameDay(created, now);
      }
      if (activeTab === "thisweek") {
        return order.orderStatus !== "cancelled" && created >= weekStart;
      }
      return true;
    });
  }, [orders, activeTab]);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300 overflow-y-auto"
      onClick={() => setIsOrderHistoryOpen(false)}
    >
      <div
        className="fixed right-0 top-0 h-full w-full max-w-[450px] bg-white dark:bg-gray-900 transform duration-300 ease-in-out rounded-l-[20px] shadow-[-23px_0_72px_0_rgba(0,0,0,0.15)] overflow-y-auto flex flex-col transition-colors"
        style={{ animation: "slideInRight 0.3s ease-out" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full inline-flex flex-col items-start rounded-l-[24px] bg-white dark:bg-gray-900 flex-1 transition-colors">
          <div className="w-full flex items-center justify-between p-6 pb-4 border-b border-gray-200 dark:border-gray-700 transition-colors">
            <div className="flex items-center gap-3">
              <button onClick={() => setIsOrderHistoryOpen(false)} className="cursor-pointer">
                <img src={arrowLeft} alt="back" className="flex w-[36px] h-[36px] justify-center items-center gap-[7.5px] shrink-0 rounded-full border-[0.75px] border-[rgba(112,107,102,0.25)] dark:border-gray-600 bg-[rgba(255,255,255,0.25)] dark:bg-gray-800 cursor-pointer transition-colors" />
              </button>
              <h2 className="text-[#040404] dark:text-gray-200 font-Urbanist text-[20px] font-semibold leading-[28px] transition-colors">
                Order History
              </h2>
            </div>
            <button onClick={() => setIsOrderHistoryOpen(false)} className="cursor-pointer">
              <img src={cancel} alt="close" className="flex w-[36px] h-[36px] justify-center items-center gap-[7.5px] shrink-0 rounded-full border-[0.75px] border-[rgba(112,107,102,0.25)] dark:border-gray-600 bg-[rgba(255,255,255,0.25)] dark:bg-gray-800 cursor-pointer transition-colors" />
            </button>
          </div>

          <div className="w-full px-4 py-3 flex items-center gap-3 border-gray-200 overflow-x-auto">
            <button
              onClick={() => setActiveTab("filters")}
              className={`flex items-center gap-2 px-3 py-2 rounded-[10px] border transition-all duration-200
                ${
                  activeTab === "filters"
                    ? "bg-[#FF8A00] text-white border-[#FF8A00]"
                    : "bg-white text-gray-700 border-gray-300 hover:border-[#FF8A00]"
                }`}
            >
              <img src={filter} alt="filter" className="h-[16px] w-[16px]" />
              <span className="text-[16px] font-medium leading-[20px]">Filters</span>
              <img src={downArrow} alt="down arrow" className="h-[16px] w-[16px]" />
            </button>

            {["today", "thisweek", "cancelled"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 py-2 rounded-[10px] border text-sm font-medium transition-all duration-200 capitalize
                ${
                  activeTab === tab
                    ? "bg-[#FF8A00] text-white border-[#FF8A00]"
                    : "bg-white text-gray-700 border-gray-300 hover:border-[#FF8A00]"
                }`}
              >
                {tab === "thisweek" ? "This Week" : tab}
              </button>
            ))}
          </div>

          <div className="w-full px-6 overflow-y-auto flex-1">
            {loading ? (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF8A00]"></div>
              </div>
            ) : filteredOrders.length === 0 ? (
              <p className="text-center text-gray-500 py-16">No orders found for this filter</p>
            ) : (
              filteredOrders.map((order, index) => (
                <div
                  key={order._id}
                  onClick={() => setSelectedOrderIndex(index)}
                  className="cursor-pointer transition-colors mb-4"
                >
                  <Order
                    order={order}
                    selectedOrderIndex={selectedOrderIndex ?? -1}
                    index={index}
                    tab={activeTab === "cancelled" ? "cancelled" : "delivered"}
                  />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderHistory;
